import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Settings,
  Image,
  Palette,
  Globe,
  Save,
  RefreshCw,
  Building2,
  Smartphone,
  Check
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const BuilderSettingsPage = () => {
  const { token, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [builders, setBuilders] = useState([]);
  const [selectedBuilderId, setSelectedBuilderId] = useState('');
  
  const isPlatformAdmin = user?.tenant_role === 'platform_admin' || 
                          user?.role_id === 'role_platform_admin' ||
                          user?.role_id === 'role_developer';
  
  const [settings, setSettings] = useState({
    builder_id: '',
    builder_name: '',
    branding: {
      logo_url: '',
      logo_link_url: '',
      favicon_url: '',
      pwa_icon_url: '',
      primary_color: '',
      secondary_color: '',
      page_title: '',
      page_description: ''
    },
    has_custom_branding: false
  });
  
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    if (isPlatformAdmin) {
      fetchBuilders();
    } else if (user?.builder_id) {
      setSelectedBuilderId(user.builder_id);
      fetchSettings(user.builder_id);
    }
  }, [user]);

  useEffect(() => {
    if (selectedBuilderId) {
      fetchSettings(selectedBuilderId);
    }
  }, [selectedBuilderId]);

  const fetchBuilders = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/builders/`, { headers });
      setBuilders(response.data);
      if (response.data.length > 0 && !selectedBuilderId) {
        setSelectedBuilderId(response.data[0].id);
      }
    } catch (error) {
      console.error('Failed to fetch builders:', error);
    }
  };

  const fetchSettings = async (builderId) => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/settings/builder/${builderId}`, { headers });
      setSettings(response.data);
    } catch (error) {
      console.error('Failed to fetch settings:', error);
      toast.error('Failed to load builder settings');
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async () => {
    if (!selectedBuilderId) return;
    
    setSaving(true);
    try {
      await axios.put(
        `${API_URL}/api/settings/builder/${selectedBuilderId}`,
        settings.branding,
        { headers }
      );
      toast.success('Builder branding saved successfully!');
      fetchSettings(selectedBuilderId);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const updateBranding = (field, value) => {
    setSettings({
      ...settings,
      branding: {
        ...settings.branding,
        [field]: value
      }
    });
  };

  const clearField = (field) => {
    updateBranding(field, '');
  };

  if (loading && !selectedBuilderId) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-600"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="builder-settings-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-3">
              <Building2 className="w-7 h-7 text-blue-600" />
              Builder Branding Settings
            </h1>
            <p className="text-gray-500 dark:text-gray-400 mt-1">
              Customize branding for builder portals
            </p>
          </div>
          <Button onClick={saveSettings} disabled={saving || !selectedBuilderId} data-testid="save-builder-settings">
            {saving ? (
              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Save className="w-4 h-4 mr-2" />
            )}
            {saving ? 'Saving...' : 'Save Settings'}
          </Button>
        </div>

        {/* Builder Selector (Platform Admin Only) */}
        {isPlatformAdmin && builders.length > 0 && (
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-4">
                <Label className="text-gray-700 dark:text-gray-200 whitespace-nowrap">Select Builder:</Label>
                <Select value={selectedBuilderId} onValueChange={setSelectedBuilderId}>
                  <SelectTrigger className="w-64 bg-white dark:bg-gray-800 text-gray-900 dark:text-white">
                    <SelectValue placeholder="Select a builder..." />
                  </SelectTrigger>
                  <SelectContent className="bg-white dark:bg-gray-800">
                    {builders.map(builder => (
                      <SelectItem key={builder.id} value={builder.id} className="text-gray-900 dark:text-white">
                        {builder.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {settings.has_custom_branding && (
                  <Badge className="bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                    <Check className="w-3 h-3 mr-1" />
                    Custom Branding Active
                  </Badge>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-600"></div>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Logo Settings */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Image className="w-5 h-5 text-blue-600" />
                  Logo Settings
                </CardTitle>
                <CardDescription>
                  Custom logos for {settings.builder_name || 'this builder'}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Logo URL */}
                <div>
                  <Label className="text-gray-700 dark:text-gray-200">Logo URL</Label>
                  <div className="flex gap-2 mt-1">
                    <Input
                      placeholder="https://example.com/logo.png"
                      value={settings.branding.logo_url || ''}
                      onChange={(e) => updateBranding('logo_url', e.target.value)}
                      className="flex-1"
                    />
                    {settings.branding.logo_url && (
                      <Button variant="outline" size="sm" onClick={() => clearField('logo_url')}>
                        Clear
                      </Button>
                    )}
                  </div>
                  {settings.branding.logo_url && (
                    <div className="mt-2 p-3 bg-gray-100 dark:bg-gray-800 rounded-lg">
                      <img 
                        src={settings.branding.logo_url} 
                        alt="Logo Preview" 
                        className="max-h-12 object-contain"
                        onError={(e) => e.target.style.display = 'none'}
                      />
                    </div>
                  )}
                </div>

                {/* Logo Link URL */}
                <div>
                  <Label className="text-gray-700 dark:text-gray-200">Logo Link URL</Label>
                  <Input
                    placeholder="/"
                    value={settings.branding.logo_link_url || ''}
                    onChange={(e) => updateBranding('logo_link_url', e.target.value)}
                    className="mt-1"
                  />
                  <p className="text-xs text-gray-500 mt-1">Where the logo links when clicked</p>
                </div>

                {/* Favicon URL */}
                <div>
                  <Label className="text-gray-700 dark:text-gray-200">Favicon URL</Label>
                  <div className="flex gap-2 mt-1">
                    <Input
                      placeholder="https://example.com/favicon.ico"
                      value={settings.branding.favicon_url || ''}
                      onChange={(e) => updateBranding('favicon_url', e.target.value)}
                      className="flex-1"
                    />
                    {settings.branding.favicon_url && (
                      <img 
                        src={settings.branding.favicon_url} 
                        alt="Favicon" 
                        className="w-8 h-8 object-contain"
                        onError={(e) => e.target.style.display = 'none'}
                      />
                    )}
                  </div>
                </div>

                {/* PWA Icon URL */}
                <div>
                  <Label className="text-gray-700 dark:text-gray-200">PWA Icon URL</Label>
                  <div className="flex gap-2 mt-1">
                    <Input
                      placeholder="https://example.com/icon-512.png"
                      value={settings.branding.pwa_icon_url || ''}
                      onChange={(e) => updateBranding('pwa_icon_url', e.target.value)}
                      className="flex-1"
                    />
                    {settings.branding.pwa_icon_url && (
                      <img 
                        src={settings.branding.pwa_icon_url} 
                        alt="PWA Icon" 
                        className="w-10 h-10 object-contain rounded-lg"
                        onError={(e) => e.target.style.display = 'none'}
                      />
                    )}
                  </div>
                  <p className="text-xs text-gray-500 mt-1">512x512 PNG recommended</p>
                </div>
              </CardContent>
            </Card>

            {/* Color & SEO Settings */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Palette className="w-5 h-5 text-blue-600" />
                  Colors & SEO
                </CardTitle>
                <CardDescription>
                  Brand colors and page metadata
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Primary Color */}
                <div>
                  <Label className="text-gray-700 dark:text-gray-200">Primary Color</Label>
                  <div className="flex gap-2 mt-1">
                    <Input
                      type="color"
                      value={settings.branding.primary_color || '#2563EB'}
                      onChange={(e) => updateBranding('primary_color', e.target.value)}
                      className="w-14 h-10 p-1 cursor-pointer"
                    />
                    <Input
                      value={settings.branding.primary_color || ''}
                      onChange={(e) => updateBranding('primary_color', e.target.value)}
                      placeholder="Leave empty for platform default"
                      className="flex-1"
                    />
                  </div>
                </div>

                {/* Secondary Color */}
                <div>
                  <Label className="text-gray-700 dark:text-gray-200">Secondary Color</Label>
                  <div className="flex gap-2 mt-1">
                    <Input
                      type="color"
                      value={settings.branding.secondary_color || '#F97316'}
                      onChange={(e) => updateBranding('secondary_color', e.target.value)}
                      className="w-14 h-10 p-1 cursor-pointer"
                    />
                    <Input
                      value={settings.branding.secondary_color || ''}
                      onChange={(e) => updateBranding('secondary_color', e.target.value)}
                      placeholder="Leave empty for platform default"
                      className="flex-1"
                    />
                  </div>
                </div>

                {/* Color Preview */}
                <div className="p-4 bg-gray-100 dark:bg-gray-800 rounded-lg">
                  <p className="text-xs text-gray-500 mb-2">Preview</p>
                  <div className="flex gap-3">
                    <button 
                      className="px-3 py-1.5 rounded text-white text-sm"
                      style={{ backgroundColor: settings.branding.primary_color || '#2563EB' }}
                    >
                      Primary
                    </button>
                    <button 
                      className="px-3 py-1.5 rounded text-white text-sm"
                      style={{ backgroundColor: settings.branding.secondary_color || '#F97316' }}
                    >
                      Secondary
                    </button>
                  </div>
                </div>

                {/* Page Title */}
                <div>
                  <Label className="text-gray-700 dark:text-gray-200">Page Title</Label>
                  <Input
                    placeholder={settings.builder_name || 'Builder name'}
                    value={settings.branding.page_title || ''}
                    onChange={(e) => updateBranding('page_title', e.target.value)}
                    className="mt-1"
                  />
                </div>

                {/* Page Description */}
                <div>
                  <Label className="text-gray-700 dark:text-gray-200">Page Description</Label>
                  <textarea
                    className="w-full mt-1 px-3 py-2 border rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white dark:border-gray-600"
                    rows={3}
                    placeholder="Description for search engines..."
                    value={settings.branding.page_description || ''}
                    onChange={(e) => updateBranding('page_description', e.target.value)}
                  />
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Info Card */}
        <Card className="bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <Globe className="w-5 h-5 text-blue-600 mt-0.5" />
              <div>
                <h4 className="font-medium text-blue-900 dark:text-blue-200">Inheritance</h4>
                <p className="text-sm text-blue-700 dark:text-blue-300">
                  Empty fields will inherit from Platform Settings. Sales Companies under this builder 
                  will inherit these settings unless they set their own.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default BuilderSettingsPage;
